import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusyOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ActionsMenu, {
  type ActionsMenuItem,
} from "components/botsManagement/ActionsMenu";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useGetAdminTaskQuery,
  useGetBotRegistryQuery,
  useRunTaskMutation,
  useStopTaskMutation,
  useReduceJobTasksMutation,
  useUpdateAdminTaskMutation,
  useSubmitTask2FACodeMutation,
  useGetTask2FAStatusQuery,
  useGetBackendUserBotConnectionsQuery,
  useLazyGetTaskAllLogsQuery,
  useLazyGetTaskCloudWatchLogsQuery,
  useGetTaskLiveViewQuery,
  useScheduleTaskRunMutation,
  useCancelTaskRunScheduleMutation,
} from "features/adminUsers/adminUsers";
import type { ScheduledRunInfo } from "features/adminUsers/adminUsers";
import {
  ensureFreshAccessToken,
  readStoredAccessToken,
} from "features/auth/tokenRefresh";
import { useAppDispatch, useAppSelector } from "features/store";
import useViewAs, { CLIENT_VIEW_READ_ONLY_TOOLTIP } from "hooks/useViewAs";
import { isBotsOperator } from "utils/roles";
import { botsManagementWs } from "features/ws/botsManagementWs";
import AppDialog from "components/common/AppDialog";
import CustomScrollbars from "components/CustomScrollbars";
import WorkspaceMetaList, {
  type WorkspaceMetaRow,
} from "components/botsManagement/WorkspaceMetaList";
import TaskLiveViewDialog from "components/botsManagement/TaskLiveViewDialog";
import ScheduleRunDialog from "components/botsManagement/ScheduleRunDialog";
import {
  scheduledRunTagLabel,
  scheduledRunTooltip,
} from "components/botsManagement/scheduledRuns";
import LogsView, { LogsScope } from "components/botsManagement/LogsView";
import useResponsive from "hooks/useResponsive";
import { viewportCappedHeightSx } from "config";
import {
  isLiveViewNeeded,
  isLiveViewReady,
  shouldAutoOpenLiveView,
} from "components/botsManagement/liveViewPrompt";
import {
  isTwoFaAutoPhase,
  isTwoFaCodeNeeded,
  isTwoFaCodeSubmitted,
} from "components/botsManagement/twoFaPrompt";
import {
  isRunHoldEvent,
  parseRunHeldBy,
  runHeldReason,
  type RunHoldWatch,
} from "components/botsManagement/runHold";
import {
  ChipColor,
  botWorkStatusChipColor,
  taskRunStatusChipColor,
} from "utils/botStatusColors";
import EmployeeProgressBar from "components/botsManagement/EmployeeProgressBar";
import { DETAIL_CARD_SX, DETAIL_LABEL_SX } from "components/botsManagement/detailCard";
import {
  countersSource,
  taskCountersTooltip,
} from "components/botsManagement/employeeCounters";
import MetaPill, { type MetaTone } from "components/botsManagement/MetaPill";
import { botKindMeta } from "components/botsManagement/botKind";
import SoftTag from "components/botsManagement/SoftTag";
import BotOptionSummary, {
  botOptionSpecsFor,
  summarizeBotOptions,
} from "components/botsManagement/BotOptionSummary";
import BusinessIcon from "@mui/icons-material/Business";
import LayersIcon from "@mui/icons-material/Layers";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import SmartToyIcon from "@mui/icons-material/SmartToyOutlined";
import StraightenIcon from "@mui/icons-material/StraightenOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { taskLogsWs, TaskLogsWsMessage, TaskLogEvent } from "features/ws/taskLogsWs";

const RUNNING_STATUSES = ["ECS_CONTAINER_STARTED", "PROCESSING"];
// A queued task is in flight too: its Stop takes it out of the line, as on the panel.
const STOP_STATUSES = [...RUNNING_STATUSES, "QUEUED"];

function getErrorMessage(error: unknown, fallback: string): string {
  // The admin run endpoint answers {error}, the task endpoints {message}.
  const data = (error as { data?: { message?: string; error?: string; detail?: string } })
    ?.data;
  return data?.message || data?.error || data?.detail || fallback;
}

function formatTs(ms?: number | null): string {
  if (!ms) return "";
  try {
    return new Date(ms).toISOString().replace("T", " ").replace("Z", "");
  } catch {
    return "";
  }
}

function chipColorTone(color: ChipColor): MetaTone {
  return color === "default" ? "neutral" : (color as MetaTone);
}



// Light, AWS-CloudWatch-like log surface: the frame only holds the height.
const LOG_BOX_SX = {
  m: 0,
  bgcolor: "#ffffff",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  display: "flex",
  overflow: "hidden",
};

const LOG_TEXT_SX = {
  fontFamily: "monospace",
  fontSize: "0.78rem",
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  color: "#1a1a1a",
  p: 1.5,
};

// Fixed, generous log viewport (AWS-CloudWatch-like). The box never grows past
// this — overflow scrolls inside it instead of stretching the workspace.
/**
 * The log window never grows past its designed height, and on a laptop it
 * shrinks instead of pushing the controls under it off the screen: a 768-tall
 * display gives the page about 170px once the chrome has taken its cut.
 */
const LOG_BOX_SIZING = viewportCappedHeightSx(180, 460, 430);

// ---------------------------------------------------------------------------
// Live logs tab
// ---------------------------------------------------------------------------

// AWS-CloudWatch-style log line: gray timestamp, error lines red.
const LEADING_TS_RX =
  /^\s*\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?\s*Z?\s*(?:[-|:]?\s*)?/;
const ERROR_RX = /(?:\b|^)(ERROR|CRITICAL|Traceback|Exception)\b/i;

function stripLeadingTs(msg: string): string {
  const m = LEADING_TS_RX.exec(msg);
  if (!m) return msg;
  return msg.slice(m[0].length);
}

function LogLine({ event }: { event: TaskLogEvent }) {
  const raw = event.message ?? "";
  const text = stripLeadingTs(raw);
  const isError = ERROR_RX.test(text);
  const ts = event.timestamp ? formatTs(event.timestamp) : "";
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        py: 0.15,
        px: 0.5,
        borderRadius: 0.5,
        "&:hover": { bgcolor: "#f5f5f5" },
        fontFamily: "monospace",
        fontSize: "0.78rem",
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        color: isError ? "#d32f2f" : "#1a1a1a",
      }}
    >
      {ts ? (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            color: "#5f6b7a",
            fontSize: "0.74rem",
          }}
        >
          {ts}
        </Box>
      ) : null}
      <Box component="span" sx={{ flex: 1 }}>
        {text}
      </Box>
    </Box>
  );
}

const RANGE_PRESETS: { key: string; label: string; ms: number | null }[] = [
  { key: "live", label: "Live tail (latest)", ms: null },
  { key: "1m", label: "Last 1 minute", ms: 60_000 },
  { key: "5m", label: "Last 5 minutes", ms: 5 * 60_000 },
  { key: "15m", label: "Last 15 minutes", ms: 15 * 60_000 },
  { key: "30m", label: "Last 30 minutes", ms: 30 * 60_000 },
  { key: "1h", label: "Last 1 hour", ms: 60 * 60_000 },
  { key: "3h", label: "Last 3 hours", ms: 3 * 60 * 60_000 },
  { key: "12h", label: "Last 12 hours", ms: 12 * 60 * 60_000 },
  { key: "1d", label: "Last 24 hours", ms: 24 * 60 * 60_000 },
  { key: "all", label: "All available", ms: -1 },
  { key: "custom", label: "Custom range…", ms: 0 },
];

const CLEAR_LS_PREFIX = "taskLogs.cleared.";
const clearKey = (id: number) => `${CLEAR_LS_PREFIX}${id}`;

function readClearedAt(taskId: number): number | null {
  try {
    const raw = localStorage.getItem(clearKey(taskId));
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

const LIVE_TAIL_LIMIT = 5000;

/** Append without repeating a line the socket and the REST tail both carry. */
function mergeLogEvents(
  prev: TaskLogEvent[],
  incoming: TaskLogEvent[],
): TaskLogEvent[] {
  if (!incoming.length) return prev;
  const seen = new Set(prev.map((e) => `${e.timestamp}|${e.message}`));
  const fresh = incoming.filter((e) => !seen.has(`${e.timestamp}|${e.message}`));
  if (!fresh.length) return prev;
  const next = prev.concat(fresh);
  return next.length > LIVE_TAIL_LIMIT ? next.slice(next.length - LIVE_TAIL_LIMIT) : next;
}

function LiveLogsTab({
  taskId,
  ecsTaskId,
  liveAvailable,
  taskTerminal,
}: {
  taskId: number;
  ecsTaskId: string;
  liveAvailable: boolean;
  isLocalRun: boolean;
  taskTerminal: boolean;
}) {
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const dispatch = useAppDispatch();
  const [fetchCw] = useLazyGetTaskCloudWatchLogsQuery();

  // mode: live = WS streaming; history = static range view; cleared = banner only
  const [mode, setMode] = useState<"live" | "history" | "cleared">(() =>
    readClearedAt(taskId) ? "cleared" : "live",
  );
  const [clearedAt, setClearedAt] = useState<number | null>(() => readClearedAt(taskId));

  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [liveEvents, setLiveEvents] = useState<TaskLogEvent[]>([]);
  // Bumped by the manual Refresh button to force a WS reconnect (re-runs the effect).
  const [liveNonce, setLiveNonce] = useState(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const pendingBufRef = useRef<TaskLogEvent[]>([]);
  const liveBoxRef = useRef<HTMLDivElement | null>(null);

  // History view state
  const [historyEvents, setHistoryEvents] = useState<TaskLogEvent[]>([]);
  const [historyRange, setHistoryRange] = useState<{ start: number; end: number; label: string } | null>(null);
  const [historyNextToken, setHistoryNextToken] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [presetKey, setPresetKey] = useState<string>("live");
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");

  const dropPending = () => {
    pendingBufRef.current = [];
    setPendingCount(0);
  };

  // WebSocket live tail — only when mode === "live"
  useEffect(() => {
    if (!liveAvailable || mode !== "live") return;
    taskLogsWs.setTokenProvider(() => localStorage.getItem("accessToken"));
    const unStatus = taskLogsWs.subscribeStatus(setConnected);
    const unAuth = taskLogsWs.subscribeAuthRejected(() => {
      // Reconnecting on the token the socket just refused would be refused
      // again; only a different one is worth another attempt.
      const rejected = readStoredAccessToken();
      void ensureFreshAccessToken(dispatch).then(() => {
        const current = readStoredAccessToken();
        if (current && current !== rejected) taskLogsWs.connect(current, taskId);
      });
    });
    const unMsg = taskLogsWs.subscribe((msg: TaskLogsWsMessage) => {
      if (msg.type === "logs") {
        // A re-run resumes on the same socket: fresh lines cancel the previous
        // run's "ended" state (and re-open the REST fallback gate below).
        setEnded((prev) => (prev != null ? null : prev));
        const incoming = msg.events;
        if (pausedRef.current) {
          // Buffer while paused so Resume reveals everything (no data loss).
          const buf = pendingBufRef.current.concat(incoming);
          pendingBufRef.current = buf.length > 5000 ? buf.slice(buf.length - 5000) : buf;
          setPendingCount(pendingBufRef.current.length);
          return;
        }
        setLiveEvents((prev) => mergeLogEvents(prev, incoming));
      } else if (msg.type === "end") {
        setEnded(msg.status || "finished");
      } else if (msg.type === "unavailable") {
        setUnavailable(true);
      }
    });

    const token = accessToken || localStorage.getItem("accessToken");
    if (token) taskLogsWs.connect(token, taskId);

    return () => {
      unStatus();
      unAuth();
      unMsg();
      taskLogsWs.disconnect();
      taskLogsWs.setTokenProvider(null);
    };
  }, [taskId, ecsTaskId, liveAvailable, mode, accessToken, dispatch, liveNonce]);

  // Polling tail for environments without a WebSocket service - the socket
  // never opens there and the tab would stay empty.
  const pollCursorRef = useRef<number | null>(null);
  const backfilledRef = useRef<number | null>(null);

  // The live tail only carries what happens next, so the tab opened empty and
  // needed a manual Refresh to show anything. Seed it with the recent tail.
  useEffect(() => {
    if (!liveAvailable || mode !== "live") return;
    if (backfilledRef.current === taskId) return;
    backfilledRef.current = taskId;
    let cancelled = false;
    void (async () => {
      try {
        const end = Date.now();
        const res = await fetchCw({ taskId, start: end - 15 * 60 * 1000, end }).unwrap();
        const events = res.events ?? [];
        if (cancelled || !events.length) return;
        pollCursorRef.current = (events[events.length - 1]?.timestamp ?? end) + 1;
        setLiveEvents((prev) => mergeLogEvents(prev, events));
      } catch {
        /* the socket or the poll below will fill it in */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, liveAvailable, mode, fetchCw]);
  useEffect(() => {
    if (!liveAvailable || mode !== "live" || connected || ended) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const pull = async () => {
      try {
        const start = pollCursorRef.current ?? Date.now() - 10 * 60 * 1000;
        const end = Date.now();
        const res = await fetchCw({ taskId, start, end }).unwrap();
        const events = res.events ?? [];
        if (!cancelled && events.length) {
          pollCursorRef.current = (events[events.length - 1]?.timestamp ?? end) + 1;
          setLiveEvents((prev) => mergeLogEvents(prev, events));
        } else if (!cancelled) {
          pollCursorRef.current = start;
        }
      } catch {
        /* the next tick retries */
      }
      if (!cancelled) timer = setTimeout(pull, 5000);
    };

    pull();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [taskId, ecsTaskId, liveAvailable, mode, connected, ended, fetchCw]);

  // A re-run assigns a fresh ECS task id — drop the previous run's buffered lines
  // so the live viewport reflects the new run without a manual page refresh.
  const prevEcsRef = useRef(ecsTaskId);
  useEffect(() => {
    if (prevEcsRef.current === ecsTaskId) return;
    prevEcsRef.current = ecsTaskId;
    setLiveEvents([]);
    pendingBufRef.current = [];
    setPendingCount(0);
    setEnded(null);
    setUnavailable(false);
    pollCursorRef.current = null;
  }, [ecsTaskId]);

  // Auto-scroll live box
  useEffect(() => {
    if (paused || mode !== "live") return;
    const el = liveBoxRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [liveEvents, paused, mode]);

  const runHistoryQuery = async (start: number, end: number, label: string, append = false) => {
    setHistoryLoading(true);
    try {
      const res = await fetchCw({
        taskId,
        start,
        end,
        nextToken: append ? historyNextToken ?? undefined : undefined,
      }).unwrap();
      const events = res.events ?? [];
      setHistoryEvents((prev) => (append ? prev.concat(events) : events));
      setHistoryNextToken(res.next_token ?? null);
      if (!append) {
        setHistoryRange({ start, end, label });
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const switchToLive = () => {
    setMode("live");
    setHistoryEvents([]);
    setHistoryRange(null);
    setHistoryNextToken(null);
    setPresetKey("live");
    if (clearedAt != null) {
      try {
        localStorage.removeItem(clearKey(taskId));
      } catch {
        /* noop */
      }
      setClearedAt(null);
    }
  };

  const applyPreset = async (key: string) => {
    setPresetKey(key);
    if (key === "live") {
      switchToLive();
      return;
    }
    if (key === "custom") {
      // wait for Apply
      return;
    }
    const preset = RANGE_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    const end = Date.now();
    let start: number;
    let label: string;
    if (preset.ms === -1) {
      // "All available" — ask backend for stream range first
      try {
        const probe = await fetchCw({ taskId }).unwrap();
        if (probe.local || !probe.range) {
          setHistoryEvents([]);
          setHistoryRange({ start: 0, end, label: "All available" });
          setMode("history");
          if (clearedAt != null) {
            try { localStorage.removeItem(clearKey(taskId)); } catch { /* noop */ }
            setClearedAt(null);
          }
          return;
        }
        start = probe.range.first ?? end - 24 * 60 * 60_000;
        label = "All available";
      } catch {
        start = end - 24 * 60 * 60_000;
        label = "All available";
      }
    } else {
      start = end - (preset.ms ?? 0);
      label = preset.label;
    }
    setMode("history");
    if (clearedAt != null) {
      try { localStorage.removeItem(clearKey(taskId)); } catch { /* noop */ }
      setClearedAt(null);
    }
    await runHistoryQuery(start, end, label, false);
  };

  const applyCustomRange = async () => {
    const s = new Date(customStart).getTime();
    const e = new Date(customEnd).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || s >= e) return;
    setMode("history");
    if (clearedAt != null) {
      try { localStorage.removeItem(clearKey(taskId)); } catch { /* noop */ }
      setClearedAt(null);
    }
    await runHistoryQuery(s, e, `${formatTs(s)} → ${formatTs(e)}`, false);
  };

  const onClearLogs = () => {
    setLiveEvents([]);
    dropPending();
    setPaused(false);
    const now = Date.now();
    try {
      localStorage.setItem(clearKey(taskId), String(now));
    } catch {
      /* noop */
    }
    setClearedAt(now);
    setMode("cleared");
    taskLogsWs.disconnect();
  };

  const onResume = () => {
    if (pendingBufRef.current.length) {
      setLiveEvents((prev) => mergeLogEvents(prev, pendingBufRef.current));
      dropPending();
    }
    setPaused(false);
  };

  // Force-reconnect the live tail: reset transient flags and bump the nonce so the
  // WS effect tears down and reconnects.
  const refreshLive = () => {
    setEnded(null);
    setUnavailable(false);
    setPaused(false);
    dropPending();
    setLiveNonce((n) => n + 1);
  };

  // Reset transient state when switching tasks
  useEffect(() => {
    setLiveEvents([]);
    dropPending();
    setHistoryEvents([]);
    setHistoryRange(null);
    setHistoryNextToken(null);
    setEnded(null);
    setUnavailable(false);
    setPaused(false);
    const cleared = readClearedAt(taskId);
    setClearedAt(cleared);
    setMode(cleared ? "cleared" : "live");
    setPresetKey("live");
  }, [taskId]);

  if (!liveAvailable || unavailable) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Live logs are not available yet. Status/file logs and task controls remain available.
      </Alert>
    );
  }

  const showLiveViewport = mode === "live";
  const showHistoryViewport = mode === "history";
  const showClearedBanner = mode === "cleared";

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle2">Logs</Typography>
          {mode === "live" ? (
            <Chip
              size="small"
              label={connected ? "Live" : "Live (polling)"}
              color={connected ? "success" : "default"}
              variant="outlined"
            />
          ) : (
            <Chip size="small" label={mode === "history" ? "History" : "Cleared"} variant="outlined" />
          )}
          {ended && (
            <Chip size="small" label={`Ended: ${ended}`} color="info" variant="outlined" />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            select
            size="small"
            value={presetKey}
            onChange={(e) => void applyPreset(e.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            {RANGE_PRESETS.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {mode === "live" && (
            <>
              <Button size="small" startIcon={<RefreshIcon />} onClick={refreshLive}>
                Refresh
              </Button>
              <Button
                size="small"
                onClick={() => (paused ? onResume() : setPaused(true))}
                color={paused && pendingCount > 0 ? "warning" : "primary"}
              >
                {paused ? `Resume${pendingCount ? ` (${pendingCount})` : ""}` : "Pause"}
              </Button>
              <Button size="small" onClick={onClearLogs}>
                Clear
              </Button>
            </>
          )}
          {mode === "history" && (
            <Button size="small" onClick={switchToLive}>
              Back to live
            </Button>
          )}
        </Stack>

        {presetKey === "custom" && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              size="small"
              type="datetime-local"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <TextField
              size="small"
              type="datetime-local"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
            <Button size="small" variant="outlined" onClick={() => void applyCustomRange()}>
              Apply
            </Button>
          </Stack>
        )}

        {taskTerminal && mode === "live" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This task has finished — the live tail won&apos;t update. Use the dropdown above to browse a time range.
          </Typography>
        )}

        {showLiveViewport && (
          <Box sx={{ ...LOG_BOX_SX, ...LOG_BOX_SIZING }}>
            <CustomScrollbars ref={liveBoxRef}>
              <Box sx={LOG_TEXT_SX}>
                {liveEvents.length ? (
                  liveEvents.map((ev, idx) => <LogLine key={idx} event={ev} />)
                ) : (
                  <Typography variant="body2" sx={{ color: "#5f6b7a", fontFamily: "monospace" }}>
                    {taskTerminal
                      ? "(task finished — pick a time range above to view stored logs)"
                      : "(waiting for new lines…)"}
                  </Typography>
                )}
              </Box>
            </CustomScrollbars>
          </Box>
        )}

        {showHistoryViewport && (
          <Box>
            {historyRange && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                Showing: {historyRange.label}
              </Typography>
            )}
            <Box sx={{ ...LOG_BOX_SX, ...LOG_BOX_SIZING }}>
              <CustomScrollbars>
                <Box sx={LOG_TEXT_SX}>
                  {historyLoading && historyEvents.length === 0 ? (
                    <CircularProgress size={18} sx={{ m: 1 }} />
                  ) : historyEvents.length ? (
                    historyEvents.map((ev, idx) => <LogLine key={idx} event={ev} />)
                  ) : (
                    <Typography variant="body2" sx={{ color: "#5f6b7a", fontFamily: "monospace" }}>
                      (no records in this range)
                    </Typography>
                  )}
                </Box>
              </CustomScrollbars>
            </Box>
            {historyNextToken && historyRange && (
              <Button
                size="small"
                disabled={historyLoading}
                onClick={() =>
                  void runHistoryQuery(historyRange.start, historyRange.end, historyRange.label, true)
                }
                sx={{ mt: 0.5 }}
              >
                Load more
              </Button>
            )}
          </Box>
        )}

        {showClearedBanner && (
          <Alert
            severity="info"
            sx={{ mt: 1 }}
            action={
              <Button size="small" onClick={switchToLive}>
                Resume live
              </Button>
            }
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              Logs were cleared{clearedAt ? ` on ${new Date(clearedAt).toLocaleString()}` : ""}. Pick a range to reload, or load everything.
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              {["5m", "15m", "1h", "3h", "all"].map((k) => {
                const p = RANGE_PRESETS.find((x) => x.key === k);
                if (!p) return null;
                return (
                  <Button
                    key={k}
                    size="small"
                    variant="outlined"
                    onClick={() => void applyPreset(k)}
                  >
                    {k === "all" ? "Load all logs" : `Last ${k}`}
                  </Button>
                );
              })}
            </Stack>
          </Alert>
        )}
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Task workspace (rendered inside Automations, in place of Major job workspace)
// ---------------------------------------------------------------------------

export type TaskWorkspaceProps = {
  taskId: number;
  userId: number;
  /** Return to the major-job view. The workspace passes the task's own home
   *  (major job + job) so a directly opened task link still has somewhere
   *  sensible to go when the URL carries no ?back=. */
  onBack: (fallbackPath?: string | null, jobId?: number | null) => void;
  /** Jump to the connections screen for this user. */
  onManageConnections: () => void;
};

function TaskWorkspace({ taskId, userId, onBack, onManageConnections }: TaskWorkspaceProps) {
  const theme = useTheme();
  const { isCardMode: isPhone, isShort } = useResponsive();
  // A laptop window and a phone both run out of height long before the logs
  // get any: the settled facts fold away and open on request.
  const foldMeta = isPhone || isShort;
  const [metaOpen, setMetaOpen] = useState(false);
  const [botOptionsOpen, setBotOptionsOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const { profile: botsProfile, readOnly: botsReadOnly, realProfile } = useViewAs();
  const canEditBots = isBotsOperator(botsProfile) && !botsReadOnly;
  // The initiator comparison and the live-view button follow the REAL
  // operator, and never Client View: the backend URL gate
  // (TaskLiveViewStatusSerializer._viewer_may_control) works the same way.
  const myPlatformUserId = useAppSelector((s) => s.auth.user?.id ?? null);
  const canOpenLiveView = isBotsOperator(realProfile) && !botsReadOnly;
  // This page follows the same rule as the Major Job workspace: live updates
  // arrive over the WebSocket, and polling only covers the case where it is not
  // delivering (the client reports a mute socket as disconnected). Terminal
  // tasks never poll.
  const [botsWsConnected, setBotsWsConnected] = useState(false);
  const [taskPollInterval, setTaskPollInterval] = useState(5000);
  const wsAccessToken = useAppSelector((state) => state.auth.accessToken);
  const wsEventRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: task, isFetching, refetch } = useGetAdminTaskQuery(taskId, {
    skip: !taskId,
    pollingInterval: taskPollInterval,
  });


  const [runTask] = useRunTaskMutation();
  const [stopTask] = useStopTaskMutation();
  const [reduceJobTasks] = useReduceJobTasksMutation();
  const [updateTask] = useUpdateAdminTaskMutation();
  const [submit2FA] = useSubmitTask2FACodeMutation();

  const [tab, setTab] = useState(0);
  const taskLogsScope = useMemo<LogsScope>(
    () => ({ kind: "task", taskId }),
    [taskId],
  );
  const [actionError, setActionError] = useState<string | null>(null);
  // Spinner for the manual Refresh button only. `isFetching` also flips on every
  // background poll, which made the button spin on its own — track explicit clicks.
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  // null follows the automatic rule below; true/false is the operator's choice.
  const [liveViewOverride, setLiveViewOverride] = useState<boolean | null>(null);
  const [twoFaManualOverride, setTwoFaManualOverride] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [connOpen, setConnOpen] = useState(false);
  const [connSelected, setConnSelected] = useState<number | "">("");

  const { data: connData } = useGetBackendUserBotConnectionsQuery(userId, {
    skip: !userId || !connOpen,
  });

  const taskStatus = (task?.task_status as string) || "";
  const botStatus = (task?.bot_work_status as string) || "";
  const liveAvailable = Boolean(task?.live_logs_available);
  const isLocalRun = Boolean(task?.is_local_run);
  const isSsnTask = String(task?.bot_name || "").startsWith("ssn_bots.");
  const isRunning = RUNNING_STATUSES.includes(taskStatus.toUpperCase());
  const showsStop = STOP_STATUSES.includes(taskStatus.toUpperCase());
  const taskTerminal = ["FINISHED", "STOPPED", "ERROR", "CRITICAL_ERROR"].includes(
    taskStatus.toUpperCase(),
  );

  // The task's natural home: its major job workspace with its own job
  // expanded. Used when the URL carries no ?back= (a link opened directly).
  const taskJobId = task?.job == null ? null : Number(task.job);
  const runHeldBy = parseRunHeldBy(task?.job_run_held_by);
  const runBlockedBy = runHeldReason(
    runHeldBy,
    taskId,
    String(task?.bot_name ?? "").startsWith("upload_bots."),
  );
  // Read by the socket handler, so a new holder does not resubscribe it.
  const runHoldWatchRef = useRef<RunHoldWatch>({ jobId: null, heldById: null });
  const ownWsEventRef = useRef(false);
  const heldById = runHeldBy?.id ?? null;
  useEffect(() => {
    runHoldWatchRef.current = { jobId: taskJobId, heldById };
  }, [taskJobId, heldById]);
  const taskMajorJobId = task?.major_job_id == null ? null : Number(task.major_job_id);
  const backFallbackPath =
    taskMajorJobId != null
      ? `/bots-management/${userId}/major-job/${taskMajorJobId}${
          taskJobId != null ? `?job=${taskJobId}` : ""
        }`
      : null;
  const goBack = () => onBack(backFallbackPath, taskJobId);

  useEffect(() => {
    setTaskPollInterval(taskTerminal || botsWsConnected ? 0 : 5000);
  }, [taskTerminal, botsWsConnected]);

  // 2FA visibility mirrors the Major Job workspace: bot is waiting on 2FA AND a
  // pending/ready 2FA request exists. Polled only while the socket is not
  // delivering - the window to type a code is short.
  const { data: twoFaData, refetch: refetch2FA } = useGetTask2FAStatusQuery(taskId, {
    skip: !taskId,
    pollingInterval: isRunning && !botsWsConnected ? 3000 : 0,
  });
  const { data: liveViewData, refetch: refetchLiveView } =
    useGetTaskLiveViewQuery(taskId, {
      skip: !taskId,
      pollingInterval: isRunning && !botsWsConnected ? 3000 : 0,
    });
  // Live updates for this task come from the same socket the Major Job
  // workspace uses; its events replace the timers above.
  useEffect(() => {
    if (!taskId) return undefined;
    const token = wsAccessToken || localStorage.getItem("accessToken");
    if (token) botsManagementWs.connect(token);
    const unStatus = botsManagementWs.subscribeStatus(setBotsWsConnected);
    // Status listeners only hear transitions, so a socket that was already
    // connected when this effect (re)ran - a token refresh re-runs it - would
    // leave the state stuck at false and the polls below running forever
    // alongside a healthy socket.
    setBotsWsConnected(botsManagementWs.isConnected());
    const unEvents = botsManagementWs.subscribe((event) => {
      const own = event.task?.task_id === taskId;
      // Another task matters only when it can change what Run waits for.
      if (!own && !isRunHoldEvent(event, runHoldWatchRef.current)) return;
      if (own) ownWsEventRef.current = true;
      // A status write burst emits several events back to back; one coalesced
      // pass covers them all instead of three requests per event. The
      // Success/Errors numbers ride in the task payload itself, so the
      // backend's throttled progress events move them through refetch().
      if (wsEventRefetchTimerRef.current) return;
      wsEventRefetchTimerRef.current = setTimeout(() => {
        wsEventRefetchTimerRef.current = null;
        void refetch();
        if (!ownWsEventRef.current) return;
        ownWsEventRef.current = false;
        void refetch2FA();
        void refetchLiveView();
      }, 400);
    });
    return () => {
      unStatus();
      unEvents();
      if (wsEventRefetchTimerRef.current) {
        clearTimeout(wsEventRefetchTimerRef.current);
        wsEventRefetchTimerRef.current = null;
      }
    };
  }, [taskId, wsAccessToken, refetch, refetch2FA, refetchLiveView]);

  const twoFaStatus = (twoFaData?.status || "").toLowerCase();
  const show2FA =
    botStatus.toUpperCase() === "2FA" && ["pending", "ready"].includes(twoFaStatus);
  // While the bot fetches the code automatically (auto_until in the future) the
  // manual input stays hidden; the button below lets the operator force it early.
  const twoFaAutoActive = isTwoFaAutoPhase(twoFaData, twoFaManualOverride);
  // A stored code means the bot is typing it - manual entry would send a
  // second one to a form that is already being filled.
  const twoFaSubmitted = isTwoFaCodeSubmitted(twoFaData);
  const twoFaNeeded = isTwoFaCodeNeeded(botStatus, twoFaData, twoFaManualOverride);

  // Non-terminal tasks poll via `taskPollInterval` above (WS is unreliable on Dev);
  // finished tasks refresh only via WebSocket or the manual Refresh button.

  // Close the 2FA dialog as soon as a code is no longer needed.
  useEffect(() => {
    if (!show2FA && twoFaOpen) setTwoFaOpen(false);
  }, [show2FA, twoFaOpen]);

  // Derived, never stored: storing it raced the refetch - the socket said
  // "open" while this page still held the previous status, and the close shut
  // it again. Only the operator's own choice overrides it.
  const liveViewServing = isLiveViewReady(liveViewData?.status, botStatus);
  const liveViewNeeded = isLiveViewNeeded(
    liveViewData?.status,
    liveViewData?.phase,
    botStatus,
  );
  const liveViewAutoOpen = shouldAutoOpenLiveView(
    liveViewData?.status,
    liveViewData?.phase,
    botStatus,
    liveViewData?.started_by,
    myPlatformUserId,
  );
  const liveViewOpen = liveViewOverride ?? (canOpenLiveView && liveViewAutoOpen);

  // A session that ends takes the override with it.
  useEffect(() => {
    if (!liveViewServing && liveViewOverride !== null) setLiveViewOverride(null);
  }, [liveViewServing, liveViewOverride]);

  // A fresh 2FA request starts a fresh auto phase.
  useEffect(() => {
    if (!show2FA && twoFaManualOverride) setTwoFaManualOverride(false);
  }, [show2FA, twoFaManualOverride]);


  const run = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      // One call: the backend resets a finished run itself.
      await runTask(taskId).unwrap();
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to run task."));
    }
  };

  const booking = (task?.scheduled_run ?? null) as ScheduledRunInfo | null;
  const [scheduleTaskRun, { isLoading: isScheduling }] = useScheduleTaskRunMutation();
  const [cancelTaskSchedule] = useCancelTaskRunScheduleMutation();
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const submitSchedule = async (isoInstant: string, zone: string) => {
    await scheduleTaskRun({
      taskId,
      scheduled_at: isoInstant,
      timezone: zone,
    }).unwrap();
    await refetch();
  };

  const cancelSchedule = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      await cancelTaskSchedule(taskId).unwrap();
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Could not cancel the scheduled run."));
    }
  };

  const stop = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      const report = await stopTask(taskId).unwrap();
      await refetch();
      // ECS can refuse a stop; the request still answers 200, so the only way
      // the operator learns the container may still be running is this line.
      if (report.outcome === "failed") setActionError(report.message);
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to stop task."));
    }
  };

  const doRemove = async () => {
    if (!canEditBots || taskJobId == null) return;
    setActionError(null);
    try {
      await reduceJobTasks({ jobId: taskJobId, task_ids: [taskId] }).unwrap();
      setRemoveOpen(false);
      goBack();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to remove the task."));
      setRemoveOpen(false);
    }
  };

  const doSubmit2FA = async () => {
    if (!canEditBots) return;
    setActionError(null);
    try {
      await submit2FA({ taskId, code: twoFaCode.trim() }).unwrap();
      setTwoFaOpen(false);
      setTwoFaCode("");
      // Poll until the bot consumes the code and the prompt clears.
      for (let attempt = 0; attempt < 8; attempt += 1) {
        const [, res] = await Promise.all([refetch(), refetch2FA()]);
        const fresh = (res.data?.status || "").toLowerCase();
        if (fresh !== "pending" && fresh !== "ready") break;
        await new Promise((resolve) => setTimeout(resolve, 700));
      }
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to submit 2FA code."));
    }
  };

  const doChangeConnection = async () => {
    if (!canEditBots) return;
    if (connSelected === "") return;
    setActionError(null);
    try {
      await updateTask({ taskId, body: { connection: connSelected } }).unwrap();
      setConnOpen(false);
      await refetch();
    } catch (e) {
      setActionError(getErrorMessage(e, "Failed to change connection."));
    }
  };

  const connections = connData?.connections ?? [];

  // --- Header labels (from TaskDetailSerializer) ---
  const userLabel = (task?.user_label as string) || "";
  const vendorLabel = (task?.connection_vendor as string) || "";

  // Connection pool for the picker: only logins of the same vendor as this task,
  // matching how the Major job workspace scopes its connection dropdown.
  const connPool = useMemo(() => {
    const v = vendorLabel.trim().toLowerCase();
    if (!v) return connections;
    return connections.filter(
      (c) => (c.vendor ?? "").trim().toLowerCase() === v,
    );
  }, [connections, vendorLabel]);
  const kindMeta = botKindMeta(task?.major_job_kind as string | undefined);
  const kindLabel = kindMeta.label;
  const majorJobName = (task?.major_job_name as string) || "";
  const jobName = (task?.job_name as string) || "";
  const connUsername = (task?.connection_username as string) || "";
  const taskIndex = task?.task_index_in_job as number | null | undefined;
  const taskCount = task?.task_count_in_job as number | null | undefined;

  // Sequential task number within its job, plus the database id - the key the
  // log archive, the browser profile and every refusal message use (CAL-6161).
  // The roster-only task is outside the numbering and shows the id alone.
  const taskTitle =
    String(task?.range_role ?? "") === "ROSTER_ONLY"
      ? `Roster task #${taskId}`
      : taskIndex != null
        ? `Task ${taskIndex} (#${taskId})`
        : `Task #${taskId}`;
  void taskCount;
  void userId;

  /** One list behind both the pill strip and its folded label/value form. */
  const metaRows: WorkspaceMetaRow[] = [
    ...(userLabel ? [{ title: "User", value: userLabel }] : []),
    ...(kindLabel
      ? [
          {
            title: "Action",
            value: kindLabel,
            color: kindMeta.color,
          },
        ]
      : []),
    ...(vendorLabel ? [{ title: "Vendor", value: vendorLabel }] : []),
    ...(majorJobName ? [{ title: "Major job", value: majorJobName }] : []),
    ...(jobName ? [{ title: "Task job", value: jobName }] : []),
  ];

  const goToConnections = onManageConnections;
  const ecsTaskId = (task?.ecs_task_id as string) || "";

  const [ecsCopied, setEcsCopied] = useState(false);
  const copyEcsTaskId = async () => {
    if (!ecsTaskId) return;
    try {
      await navigator.clipboard.writeText(ecsTaskId);
      setEcsCopied(true);
      window.setTimeout(() => setEcsCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Full task log history download (all_logs file). CloudWatch live logs only
  // cover one ECS task id and are lost on re-run; the all_logs file is the
  // complete history for this task.
  const [fetchAllLogs] = useLazyGetTaskAllLogsQuery();
  const [downloadingAll, setDownloadingAll] = useState(false);
  const downloadAllLogs = async () => {
    setActionError(null);
    setDownloadingAll(true);
    try {
      const res = await fetchAllLogs(taskId).unwrap();
      if (!res.found || !res.logs) {
        setActionError("No all-logs file found for this task yet.");
        return;
      }
      const blob = new Blob([res.logs], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `all_logs_task_${taskId}.log`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setActionError("Failed to download all-logs file.");
    } finally {
      setDownloadingAll(false);
    }
  };

  /** The settled actions, for a layout with one column to spend. */
  const taskMenuActions: ActionsMenuItem[] = [
    {
      key: "connection",
      label: "Change connection",
      icon: <SwapHorizIcon fontSize="small" />,
      disabled: isRunning || !canEditBots,
      hint: isRunning ? "Stop the task first to change its connection." : undefined,
      onClick: () => {
        setConnSelected((task?.connection as number) ?? "");
        setConnOpen(true);
      },
    },
    ...(isSsnTask
      ? []
      : [
          {
            key: "downloadLogs",
            label: "Download all logs",
            icon: <DownloadIcon fontSize="small" />,
            disabled: downloadingAll,
            hint: "The task's complete log history (all_logs file).",
            onClick: () => void downloadAllLogs(),
          },
        ]),
    {
      key: "remove",
      label: "Remove task",
      icon: <DeleteIcon fontSize="small" />,
      separatorBefore: true,
      danger: true,
      disabled:
        isRunning || !canEditBots || taskJobId == null || (taskCount ?? 0) <= 1,
      hint:
        taskJobId == null
          ? "This task does not belong to a job, so there is no split to take it out of."
          : (taskCount ?? 0) <= 1
            ? "A job keeps at least one task - delete the whole job instead."
            : "Takes the task out of the split; its logs stay where they are.",
      onClick: () => setRemoveOpen(true),
    },
  ];

  const rosterOnly = String(task?.range_role ?? "") === "ROSTER_ONLY";
  const rangeFrom = Number(task?.employee_number_from ?? 0);
  const rangeTo = Number(task?.employee_number_to ?? 0);
  const rangePending = !task || Boolean(task.job_ranges_pending);
  const rangeSize =
    rangePending || rosterOnly || rangeTo < rangeFrom ? null : rangeTo - rangeFrom + 1;
  // The same numbers as the task's row in the Major Job workspace: the stored
  // count plus this run's progress, or the raw counters for a never-counted task.
  const progressSuccess = Number(
    task?.success_employees_current ?? task?.successfully_processed_employees_count ?? 0,
  );
  const progressError = Number(
    task?.error_employees_current ?? task?.error_employees_count ?? 0,
  );
  const progressHint = taskCountersTooltip(
    countersSource(task?.counters_source),
    task?.job_stats_snapshot_updated_at as string | null | undefined,
  );

  const errorModeLabel = task?.error_employees_mode
    ? task?.retry_error_employees
      ? "on (automatic error pass)"
      : "on (job switch)"
    : "off";
  // The same registry the Add/Edit job dialogs render their option fields from,
  // so the page shows every option this bot declares, valued or at its default.
  const { data: botRegistry } = useGetBotRegistryQuery(
    {
      vendor: (task?.connection_vendor as string) || undefined,
      // The SSN task of a job belongs to the ssn_bots kind, not the job's own.
      jobKind: String(task?.bot_name ?? "").startsWith("ssn_bots.")
        ? "ssn_bots"
        : (task?.major_job_kind as string) || undefined,
      isActive: true,
      pageSize: 120,
    },
    { skip: !task?.connection_vendor || !task?.major_job_kind },
  );
  const botOptionLines = useMemo(
    () =>
      summarizeBotOptions(
        botOptionSpecsFor(botRegistry, String(task?.bot_name ?? "")),
        task?.additional_options as string | null | undefined,
        // Shown as its own "Document types to delete" cell below.
        ["DOCS_TYPE"],
      ),
    [botRegistry, task],
  );

  const setupItems = useMemo(() => {
    const items: [string, string, string?][] = [["Bot", (task?.bot_name as string) || "—"]];
    const kind = String(task?.bot_name ?? "").split(".")[0];
    const text = (key: string): string => {
      const value = task?.[key];
      return typeof value === "string" && value.trim() ? value.trim() : "";
    };
    if (text("document_type")) items.push(["Document type", text("document_type")]);
    if (kind === "upload_bots") {
      if (text("path_to_s3_base_folder")) {
        items.push(["Source folder", text("path_to_s3_base_folder")]);
      }
      if (text("source_vendor")) items.push(["Source vendor", text("source_vendor")]);
      items.push(["SSN match", task?.use_ssn_match === false ? "off" : "on"]);
      // The Download major job whose SSN file the bot reads as the source side.
      items.push(["Source SSN file", text("source_ssn_major_job_name") || "not chosen"]);
      // The upload switches the bot reads from this row (Edit job -> Upload
      // options): they decide whether an employee's files travel as one archive
      // and whether a stored archive is unpacked first.
      items.push(["Upload as ZIP", task?.upload_as_zip === true ? "on" : "off"]);
      items.push([
        "Unzip before uploading",
        task?.unzip_folder_before_uploading === true ? "on" : "off",
      ]);
    }
    if (kind === "delete_bots") {
      const docs = Array.isArray(task?.docs_types)
        ? (task?.docs_types as unknown[]).map(String)
        : [];
      if (docs.length) items.push(["Document types to delete", docs.join(", ")]);
    }
    items.push(["Browser profile", String(task?.browser_profile_id ?? "—")]);
    if (text("started_by_username")) items.push(["Started by", text("started_by_username")]);
    if (text("queue_run_source")) items.push(["Run source", text("queue_run_source")]);
    return items;
  }, [task]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Task workspace
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={goBack}
          sx={{ width: { xs: "100%", md: "auto" }, minHeight: { xs: 40, md: 0 } }}
        >
          Back to job
        </Button>
      </Box>

      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackGutter>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {taskTitle}
                </Typography>
                {foldMeta && (
                  <Button
                    size="small"
                    onClick={() => setMetaOpen((prev) => !prev)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transition: "transform 150ms",
                          transform: metaOpen ? "rotate(180deg)" : "none",
                        }}
                      />
                    }
                    sx={{ flexShrink: 0, textTransform: "none" }}
                    aria-expanded={metaOpen}
                  >
                    Details
                  </Button>
                )}
                {foldMeta && (
                  <Tooltip title="Refresh task stats">
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={manualRefreshing}
                        onClick={async () => {
                          setManualRefreshing(true);
                          try {
                            await Promise.all([refetch(), refetch2FA()]);
                          } finally {
                            setManualRefreshing(false);
                          }
                        }}
                      >
                        {manualRefreshing ? (
                          <CircularProgress size={18} color="primary" />
                        ) : (
                          <ReplayIcon sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
              {foldMeta ? (
                metaOpen ? (
                  <WorkspaceMetaList rows={metaRows} />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      overflowWrap: "anywhere",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {metaRows.map((row) => row.value).join(" - ")}
                  </Typography>
                )
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "stretch", mt: 0.75 }}>
                  {userLabel && (
                    <MetaPill title="User" tone="primary" icon={<PersonOutlineIcon />} value={userLabel} />
                  )}
                  {kindLabel && (
                    <MetaPill
                      title="Action"
                      tone={kindMeta.tone}
                      icon={kindMeta.icon}
                      value={kindLabel}
                    />
                  )}
                  {vendorLabel && (
                    <MetaPill title="Vendor" tone="secondary" icon={<BusinessIcon />} value={vendorLabel} />
                  )}
                  {majorJobName && (
                    <MetaPill title="Major job" icon={<LayersIcon />} value={majorJobName} />
                  )}
                  {jobName && <MetaPill title="Task job" icon={<WorkOutlineIcon />} value={jobName} />}
                </Box>
              )}
            </Box>
            <Tooltip title="Refresh task stats">
              <span style={{ display: foldMeta ? "none" : undefined }}>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={manualRefreshing}
                  onClick={async () => {
                    setManualRefreshing(true);
                    try {
                      await Promise.all([refetch(), refetch2FA()]);
                    } finally {
                      setManualRefreshing(false);
                    }
                  }}
                >
                  {manualRefreshing ? (
                    <CircularProgress size={18} color="primary" />
                  ) : (
                    <ReplayIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
            <SoftTag
              tone={chipColorTone(taskRunStatusChipColor(taskStatus))}
              label={`Status: ${taskStatus || "—"}`}
              title="Task lifecycle status"
            />
            <SoftTag
              tone={chipColorTone(botWorkStatusChipColor(botStatus))}
              icon={<SmartToyIcon />}
              label={`Bot: ${botStatus || "—"}`}
              title="Where the bot is"
            />
          </Stack>

          {!isSsnTask && (
            <Box sx={{ ...DETAIL_CARD_SX, mb: 1.25 }}>
              <EmployeeProgressBar
                success={progressSuccess}
                error={progressError}
                total={rosterOnly ? null : rangeSize}
                pendingLabel={rosterOnly ? "roster only - processes nobody" : "range pending"}
                tooltip={progressHint}
                leading={
                  <>
                    <SoftTag
                      icon={<StraightenIcon />}
                      label={
                        rosterOnly
                          ? "Roster only"
                          : rangePending
                            ? "Range -"
                            : `Range ${rangeFrom}-${rangeTo}`
                      }
                      title="The employee range this task covers now"
                    />
                    <SoftTag
                      tone={task?.error_employees_mode ? "warning" : "neutral"}
                      icon={<ReplayOutlinedIcon />}
                      label={`Error mode ${errorModeLabel}`}
                      title="In error mode a run retries only the employees whose current status is ERROR; employees no run has attempted yet are skipped."
                    />
                  </>
                }
              />
            </Box>
          )}

          {botOptionLines.length > 0 && (
            <Box sx={{ ...DETAIL_CARD_SX, mb: 1.25 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Tooltip title="What the job's Edit job form holds for this bot. Values are copied to the task when the job is saved; unset options run at their default.">
                  <Typography
                    variant="overline"
                    sx={{
                      ...DETAIL_LABEL_SX,
                      mb: foldMeta && !botOptionsOpen ? 0 : 1,
                      display: "inline-block",
                      cursor: "help",
                    }}
                  >
                    Bot options
                  </Typography>
                </Tooltip>
                {foldMeta && (
                  <Button
                    size="small"
                    onClick={() => setBotOptionsOpen((prev) => !prev)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transition: "transform 150ms",
                          transform: botOptionsOpen ? "rotate(180deg)" : "none",
                        }}
                      />
                    }
                    sx={{ textTransform: "none", flexShrink: 0 }}
                    aria-expanded={botOptionsOpen}
                  >
                    {botOptionsOpen ? "Hide" : "Show"}
                  </Button>
                )}
              </Box>
              {(!foldMeta || botOptionsOpen) && (
                <BotOptionSummary lines={botOptionLines} />
              )}
            </Box>
          )}

          <Box sx={{ ...DETAIL_CARD_SX, mb: 1.25 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="overline" sx={DETAIL_LABEL_SX}>
                Setup
              </Typography>
              {/* Settled facts that sit between the operator and the logs; on a
                  phone they open on request. */}
              {foldMeta && (
                <Button
                  size="small"
                  onClick={() => setSetupOpen((prev) => !prev)}
                  endIcon={
                    <ExpandMoreIcon
                      sx={{
                        transition: "transform 150ms",
                        transform: setupOpen ? "rotate(180deg)" : "none",
                      }}
                    />
                  }
                  sx={{ textTransform: "none", flexShrink: 0 }}
                  aria-expanded={setupOpen}
                >
                  {setupOpen ? "Hide" : "Show"}
                </Button>
              )}
            </Box>
            {(!foldMeta || setupOpen) && (
              <Box
                sx={{
                  mt: 1,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" },
                  gap: 1.5,
                }}
              >
            <Box>
              <Typography variant="overline" sx={DETAIL_LABEL_SX}>
                Connection
              </Typography>
              <Tooltip title="Manage connections">
                <Typography
                  variant="body2"
                  onClick={goToConnections}
                  sx={{
                    mt: 0.25,
                    wordBreak: "break-all",
                    color: "primary.main",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {connUsername ||
                    (task?.connection != null ? `#${task.connection}` : "—")}
                </Typography>
              </Tooltip>
            </Box>
            {setupItems.map(([label, value, hint]) => (
              <Box key={label}>
                <Typography variant="overline" sx={DETAIL_LABEL_SX}>
                  {label}
                </Typography>
                {hint ? (
                  <Tooltip title={hint}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: "break-all",
                        mt: 0.25,
                        textDecoration: "underline dotted",
                        cursor: "help",
                        width: "fit-content",
                      }}
                    >
                      {value}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography variant="body2" sx={{ wordBreak: "break-all", mt: 0.25 }}>
                    {value}
                  </Typography>
                )}
              </Box>
            ))}
            <Box>
              <Typography variant="overline" sx={DETAIL_LABEL_SX}>
                ECS task id
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {ecsTaskId || "—"}
                </Typography>
                <Tooltip title={ecsCopied ? "Copied" : "Copy ECS task id"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={copyEcsTaskId}
                      disabled={!ecsTaskId}
                      aria-label="Copy ECS task id"
                    >
                      {ecsCopied ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ mb: 1.25 }} />

          {/* Mobile: full-width stacked buttons (drawer-like); desktop: inline row. */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              "& > span": { width: { xs: "100%", md: "auto" } },
              "& .MuiButton-root": {
                width: { xs: "100%", md: "auto" },
                minHeight: { xs: 40, md: 0 },
              },
            }}
          >
          {showsStop ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<StopIcon />}
              disabled={!canEditBots}
              onClick={stop}
            >
              Stop
            </Button>
          ) : (
            <Tooltip title={runBlockedBy ?? ""}>
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={!canEditBots || runBlockedBy != null}
                  onClick={run}
                >
                  Run
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={
              booking
                ? scheduledRunTooltip(booking)
                : "Run this task at a time you pick. Slack is told 30 minutes before."
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ScheduleIcon />}
                disabled={!canEditBots}
                onClick={() => setScheduleOpen(true)}
              >
                {booking ? scheduledRunTagLabel(booking) : "Schedule"}
              </Button>
            </span>
          </Tooltip>
          {booking && (
            <Tooltip title="Cancel the scheduled run">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<EventBusyIcon />}
                  disabled={!canEditBots}
                  onClick={() => void cancelSchedule()}
                >
                  Cancel schedule
                </Button>
              </span>
            </Tooltip>
          )}
          {show2FA && twoFaAutoActive && (
            <Tooltip title="The bot is entering the 2FA code automatically. Click to enter it manually.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<CircularProgress size={14} color="warning" />}
                  disabled={!canEditBots}
                  onClick={() => setTwoFaManualOverride(true)}
                >
                  Auto 2FA...
                </Button>
              </span>
            </Tooltip>
          )}
          {show2FA && twoFaSubmitted && (
            <Tooltip title="The code is in - the bot is entering it">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<CircularProgress size={14} color="warning" />}
                  disabled
                >
                  Code sent
                </Button>
              </span>
            </Tooltip>
          )}
          {twoFaNeeded && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<VpnKeyIcon />}
              disabled={!canEditBots}
              onClick={() => setTwoFaOpen(true)}
            >
              Pass 2FA
            </Button>
          )}
          {liveViewServing && canOpenLiveView && (
            <Tooltip
              title={
                liveViewNeeded
                  ? "The bot is stuck on a captcha - solve it in its browser"
                  : "Watch the bot log in, and step in if it gets stuck"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<ImageSearchIcon />}
                  onClick={() => setLiveViewOverride(true)}
                >
                  Open live browser
                </Button>
              </span>
            </Tooltip>
          )}
          {foldMeta ? (
            <ActionsMenu
              label="More"
              ariaLabel="More task actions"
              actions={taskMenuActions}
            />
          ) : (
            <>
          <Tooltip title={isRunning ? "Stop the task first to change its connection" : ""}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapHorizIcon />}
                disabled={isRunning || !canEditBots}
                onClick={() => {
                  setConnSelected((task?.connection as number) ?? "");
                  setConnOpen(true);
                }}
              >
                Change connection
              </Button>
            </span>
          </Tooltip>
          {!isSsnTask && (
            <Tooltip title="Download the task's complete log history (all_logs file)">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    downloadingAll ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={downloadingAll}
                  onClick={downloadAllLogs}
                >
                  Download all logs
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={
              taskJobId == null
                ? "This task does not belong to a job, so there is no split to take it out of."
                : (taskCount ?? 0) <= 1
                  ? "A job keeps at least one task - delete the whole job instead."
                  : "Takes the task out of the split; its logs stay where they are."
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={
                  isRunning || !canEditBots || taskJobId == null || (taskCount ?? 0) <= 1
                }
                onClick={() => setRemoveOpen(true)}
              >
                Remove task
              </Button>
            </span>
          </Tooltip>
            </>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Live logs" />
          {!isSsnTask && <Tab label="Logs" />}
        </Tabs>
        {tab === 0 && (
          <LiveLogsTab
            taskId={taskId}
            ecsTaskId={ecsTaskId}
            liveAvailable={liveAvailable}
            isLocalRun={isLocalRun}
            taskTerminal={taskTerminal}
          />
        )}
        {!isSsnTask && tab === 1 && (
          <Box sx={{ mt: 2 }}>
            <LogsView
              scope={taskLogsScope}
              canEdit={canEditBots}
              readOnlyReason={botsReadOnly ? CLIENT_VIEW_READ_ONLY_TOOLTIP : null}
            />
          </Box>
        )}
      </Paper>

      {/* Take the task out of the job's split */}
      <AppDialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        title={<>Remove {taskTitle.toLowerCase()}?</>}
        actions={
          <>
            <Button onClick={() => setRemoveOpen(false)}>Cancel</Button>
            <Button color="error" disabled={!canEditBots} onClick={doRemove}>
              Remove
            </Button>
          </>
        }
      >
        <Typography variant="body2">
          The task leaves the job and the roster is split again across the tasks that stay.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Its log archive is kept, so the employees it already processed keep counting and
          the next run does not repeat them. A job keeps at least one task; to delete the
          logs as well, clear the job&apos;s logs or delete the job.
        </Typography>
      </AppDialog>

      {/* The operator's window into the bot's browser */}
      <TaskLiveViewDialog
        taskId={liveViewOpen && taskId ? taskId : null}
        canEdit={canEditBots}
        onClose={() => setLiveViewOverride(false)}
      />

      <ScheduleRunDialog
        open={scheduleOpen}
        what="this task"
        existing={booking}
        saving={isScheduling}
        onClose={() => setScheduleOpen(false)}
        onSubmit={submitSchedule}
      />

      <AppDialog
        open={twoFaOpen}
        onClose={() => setTwoFaOpen(false)}
        maxWidth="xs"
        title="Enter 2FA code"
        actions={
          <>
            <Button onClick={() => setTwoFaOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!twoFaCode.trim() || !canEditBots || twoFaSubmitted}
              onClick={doSubmit2FA}
            >
              Submit
            </Button>
          </>
        }
      >
        {twoFaSubmitted ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            A code has already been submitted and the bot is entering it. Wait for
            the next request before sending another one.
          </Alert>
        ) : null}
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          disabled={twoFaSubmitted}
          label="2FA code"
          value={twoFaCode}
          onChange={(e) => setTwoFaCode(e.target.value)}
          onKeyDown={(ev) => {
            if (
              ev.key === "Enter" &&
              !ev.shiftKey &&
              twoFaCode.trim().length > 0 &&
              canEditBots
            ) {
              ev.preventDefault();
              void doSubmit2FA();
            }
          }}
          inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
        />
      </AppDialog>

      {/* Change connection dialog — same login labelling as the Major job workspace. */}
      <AppDialog
        open={connOpen}
        onClose={() => setConnOpen(false)}
        maxWidth="xs"
        title="Change connection"
        actions={
          <>
            <Button onClick={() => setConnOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={connSelected === "" || !canEditBots}
              onClick={doChangeConnection}
            >
              Save
            </Button>
          </>
        }
      >
        <TextField
          select
          fullWidth
          size="small"
          label="Connection"
          margin="dense"
          value={connSelected}
          onChange={(e) => setConnSelected(Number(e.target.value))}
          sx={{
            "& .MuiSelect-select": {
              textAlign: "left",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {connPool.map((c, ix) => (
            <MenuItem key={c.id} value={c.id}>
              {`${ix + 1}. ${c.username ? `@${c.username}` : "-"}`}
            </MenuItem>
          ))}
        </TextField>
      </AppDialog>
      </Box>
      </CustomScrollbars>
    </Box>
  );
}

export default TaskWorkspace;
